import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import axios from 'redaxios'
import { config } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import { ConsoleLog } from '../../../modules/logging'
import MainModule from '../../../modules/main'

Locale.getInstance().registerKeySet('fr', 'create-contribution-add-events', {
  title: 'Événements, expositions ou festivals',
  description: `Vous devez maintenant associer votre lien de contribution à un événement, une exposition ou un festival.<br></br>Si l'événement est non répertorié sur le site de La Vitrine, vous devrez alors le créer en utilisant le formulaire dédié pour soumettre un événement, une exposition ou un festival.<br></br>Une fois créé et répertorié sur le site de La Vitrine, vous pourrez y associer votre lien de contribution.`,
  eventsListLabel: 'Événements, expositions et festivals ajoutés',
  searchLabel: "Nom de l'événement, de l'exposition ou du festival",
  searchPlaceholder: "Nom de l'événement, de l'exposition ou du festival",
  noEventsAdded:
    'Aucun événement ajouté. Utilisez la recherche ci-dessous pour ajouter des événements.',
  noOffersEventError: "Cet événement n'a pas d'offres actives, veuillez en choisir un autre",
  eventType: 'Événement',
  exhibitionType: 'Exposition',
  seriesType: 'Série',
  representationLabel: 'Représentation',
})

Locale.getInstance().registerKeySet('en', 'create-contribution-add-events', {
  title: 'Events, Exhibitions or Festivals',
  description: `Now link your Contribution to an event, exhibition, or festival.<br></br>If your event is not yet listed, create it using the appropriate registration form and submit it.<br></br>Once created and listed, you can link your Contribution to it.`,
  eventsListLabel: 'Events, exhibitions and festivals added',
  searchLabel: 'Name of the event, exhibition or festival',
  searchPlaceholder: 'Name of the event, exhibition or festival',
  noEventsAdded: 'No events added. Use the search below to add events.',
  noOffersEventError: 'This event has no active offers, please choose another one',
  eventType: 'Event',
  exhibitionType: 'Exhibition',
  seriesType: 'Series',
  representationLabel: 'Representation',
})

export interface LvcCreateContributionAddEvents {
  flowGroup: string
  flowStep: string
}

export interface AddedEventItem {
  eventData: any
  eventName: string
  addToParentEvent: boolean
  selectedOffers: any[]
}

export class LvcCreateContributionAddEvents
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private flowCore: IFlowCore | undefined
  private onEventChange: string = ''
  private isNavigatingToPreview: boolean = false

  addedEvents: AddedEventItem[] = []
  errors: Record<string, string> = {}

  attrs: Attrs = [
    ['flow-step', ''],
    ['flow-group', ''],
  ]

  reactive: Reactive = [
    ['addedEvents', []],
    ['errors', {}],
  ]

  onReady() {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)
    this.flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
    this.onEventChange = Callback.attach(this.onChangeEvent.bind(this))
    this.rerender()
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  async onChangeEvent(data: any) {
    ConsoleLog(true, 'onChangeEvent data:', data)
    const eventData = data[0]
    const id = eventData.id
    const schema = eventData.table

    try {
      if (schema === 'events') {
        // Fetch offers for the event
        const result: any = await axios.get(
          `${config.backendApi}/content/getOffersForEventV2?id=${id}&schema=events`
        )
        if (!result.data?.length) {
          // No offers, but still allow adding to parent event
          this.flowCore?.setStepData('createContributionEventPreview', {
            data: eventData,
            offers: [],
          })
        } else {
          this.flowCore?.setStepData('createContributionEventPreview', {
            data: eventData,
            offers: result.data,
          })
        }
      } else {
        // Exhibition or other types don't have offers
        this.flowCore?.setStepData('createContributionEventPreview', {
          data: eventData,
          offers: [],
        })
      }

      // Navigate to the preview substep - set flag to skip validation
      this.isNavigatingToPreview = true
      this.flowCore?.goTo('createContributionEventPreview')
    } catch (error) {
      ConsoleLog(true, 'Error fetching event offers:', error)
      MainModule.getInstance().showToastAlert(
        Locale.getKey('create-contribution-add-events.noOffersEventError'),
        3000,
        true
      )
    }
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    ConsoleLog(true, 'Add events deserialize - stepData:', stepData)
    ConsoleLog(true, 'Add events deserialize - data:', data)

    // Get any previously added events from flow data
    const contributionData = data?.createContributionAddEvents
    if (contributionData?.addedEvents) {
      this.addedEvents = contributionData.addedEvents
    }

    // Check if we're returning from the event preview substep
    const previewData = data?.createContributionEventPreview
    if (
      previewData?.data &&
      (previewData?.addToParentEvent || previewData?.selectedOffers?.length)
    ) {
      // Add the event from the preview to our list
      const eventName =
        previewData.data?.[Locale.language === 'fr' ? 'nameFr' : 'nameEn'] ||
        previewData.data?.name?.[Locale.language] ||
        ''

      const newItem: AddedEventItem = {
        eventData: previewData.data,
        eventName: eventName,
        addToParentEvent: previewData.addToParentEvent || false,
        selectedOffers: previewData.selectedOffers || [],
      }

      // Check if this event was already added (update instead of add)
      const existingIndex = this.addedEvents.findIndex(
        (e: AddedEventItem) => e.eventData?.id === previewData.data?.id
      )

      if (existingIndex >= 0) {
        // Update existing entry
        this.addedEvents = [
          ...this.addedEvents.slice(0, existingIndex),
          newItem,
          ...this.addedEvents.slice(existingIndex + 1),
        ]
      } else {
        // Add new entry
        this.addedEvents = [...this.addedEvents, newItem]
      }

      // Clear the preview data so we don't re-add on next deserialize
      this.flowCore?.setStepData('createContributionEventPreview', null)
    }

    this.rerender()
  }

  public async serialize(): Promise<unknown> {
    return {
      addedEvents: this.addedEvents,
    }
  }

  public async validate(): Promise<boolean> {
    // Skip validation when navigating to preview to add an event
    if (this.isNavigatingToPreview) {
      this.isNavigatingToPreview = false
      return true
    }

    // Require at least one event to be added
    if (!this.addedEvents?.length) {
      MainModule.getInstance().showToastAlert(
        'Veuillez ajouter au moins un événement ou une représentation.',
        3000,
        true
      )
      return false
    }
    return true
  }

  getFormattedDate(offer: any): string {
    const startDate = offer.startDate
    if (!startDate) return ''

    try {
      const dateInMs =
        typeof startDate === 'number'
          ? startDate < 10000000000
            ? startDate * 1000
            : startDate
          : new Date(startDate).getTime()

      const formattedDate = new Date(dateInMs).toLocaleDateString(Locale.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'EST',
      })

      const startTime =
        typeof offer.startTime === 'string'
          ? offer.startTime
          : offer.startTime?.[Locale.language] || ''

      return `${formattedDate}${startTime ? `, ${startTime}` : ''}`
    } catch (e) {
      return ''
    }
  }

  getOfferLocation(offer: any): string {
    return offer.location?.[Locale.language] || ''
  }

  renderAddedEvents(): string {
    if (!this.addedEvents?.length) {
      return ''
    }

    let index = 0
    return this.addedEvents
      .map((item: AddedEventItem) => {
        const items: string[] = []

        // If added to parent event - use lvc-event-block (designed for events without dates)
        if (item.addToParentEvent) {
          // Determine event type label based on table/type
          const eventTable = item.eventData?.table || ''
          let typeLabel = Locale.getKey('create-contribution-add-events.eventType')
          if (eventTable === 'exhibitionEvents' || eventTable === 'exhibitions') {
            typeLabel = Locale.getKey('create-contribution-add-events.exhibitionType')
          } else if (eventTable === 'eventSeries' || eventTable === 'series') {
            typeLabel = Locale.getKey('create-contribution-add-events.seriesType')
          }

          items.push(`
          <lvc-event-block
            event-data="${encodeURIComponent(JSON.stringify(item.eventData))}"
            event-name="${item.eventName}"
            event-type="${typeLabel}"
          ></lvc-event-block>
        `)
        }

        // Selected representations - use lvc-representation-block
        if (item.selectedOffers?.length) {
          item.selectedOffers.forEach((offer: any) => {
            // Add event name to offer for display
            const offerWithEventName = {
              ...offer,
              eventName: item.eventName,
            }
            items.push(`
            <lvc-representation-block
              representation-data="${encodeURIComponent(JSON.stringify(offerWithEventName))}"
              representation-index="${index++}"
              flow-group=""
              is-existing="true"
              show-type="true"
              hide-price="true"
              compact="true"
            ></lvc-representation-block>
          `)
          })
        }

        return items.join('')
      })
      .join('')
  }

  html = () => `
    <div class="container">
      <lvc-box mt="24px" textAlign="left">
        <lvc-text font-preset="titleMobileSmall">
          ${Locale.getKey('create-contribution-add-events.title')}
        </lvc-text>
      </lvc-box>

      <lvc-box mt="24px" textAlign="left">
        <lvc-text font-preset="medium">
          ${Locale.getKey('create-contribution-add-events.description')}
        </lvc-text>
      </lvc-box>

      <lvc-box mt="24px">
        ${
          this.addedEvents?.length
            ? `
          <lvc-text font-preset="medium">
            ${Locale.getKey('create-contribution-add-events.eventsListLabel')}
          </lvc-text>
          <lvc-box display="flex" flexDirection="column" rowGap="16px" mt="16px">
            ${this.renderAddedEvents()}
          </lvc-box>
        `
            : ''
        }
      </lvc-box>

      <lvc-box mt="24px">
        <lvc-event-search-field
          title="${Locale.getKey('create-contribution-add-events.searchLabel')}"
          placeholder="${Locale.getKey('create-contribution-add-events.searchPlaceholder')}"
          on-change="${this.onEventChange}"
          events-type="all-events"
          disable-creation="true"
        ></lvc-event-search-field>
      </lvc-box>
    </div>
  `

  css = () => `
    :host {
      display: block;
    }
  `

  eventListeners: EventListeners = []
}
